import { useState } from 'react';
import { ChevronDownIcon, PrinterIcon, ReceiptIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useAdmin } from '../AdminContext';
import { dayShort } from '../../core/format/dates';
import { printSaleReceipt } from './saleReceipt';
import { Badge } from '../ui/Badge';
import { EmptyState } from '../ui/EmptyState';
import { listBox } from '../ui/styles';
import { money } from '../../core/format/currency';
import { clock, methodLabel, refundMethodLabel } from './kassaLabels';
import { saleMethods } from '../../core/rules/kassa';
import { VoidSaleForm } from './VoidSaleForm';
import { SaleReturnForm } from './SaleReturnForm';
import { printReturnCreditNote } from './returnCreditNote';
import { staffList, withWhom } from '../../core/rules/salonDays';
import type { Sale, SaleReturn } from '../../core/types/kassa';
type Props = {
  sales: Sale[];
  editable: boolean;
  onVoided: () => void;
  /** Retour boeken (alleen de eigenaar): op elke dag, ook na de afsluiting. */
  canReturn?: boolean;
};

/** De bonnen van de dag, nieuwste bovenaan. Open een bon voor de regels, om te corrigeren of voor een retour. */
export function SaleList({
  sales,
  editable,
  onVoided,
  canReturn = false
}: Props) {
  const [open, setOpen] = useState<string | null>(null);
  const {
    site
  } = useAdmin();
  const printReturn = async (r: SaleReturn) => {
    if (!(await printReturnCreditNote(r, site.salon))) toast.error('Creditnota openen lukte niet', {
      description: 'Probeer het opnieuw of gebruik een andere browser.'
    });
  };
  const print = async (s: Sale) => {
    // Alleen bij een afspraak, en alleen in een gedeelde salon (withWhom is anders leeg).
    const performer = s.appointmentId ? withWhom(staffList(site.staff), s.staffId) : '';
    if (!(await printSaleReceipt(s, site.salon, dayShort(s.day), performer))) toast.error('Bon openen lukte niet', {
      description: 'Probeer het opnieuw of gebruik een andere browser.'
    });
  };
  if (sales.length === 0) {
    return <EmptyState icon={ReceiptIcon} title="Nog geen bonnen" text="Een afgerekende bon verschijnt hier, met het bedrag per betaalmethode in het dagoverzicht." data-id="element-2411" data-mp="14axprunom1r9" />;
  }
  return <ul className={listBox} aria-label="Bonnen" data-id="element-2412" data-mp="22mrweahffzfk">
      {sales.map(s => {
      const isOpen = open === s.id;
      const voided = s.status === 'gecorrigeerd';
      const panelId = `bon-${s.id}`;
      return <li key={s.id} data-id="element-2413" data-mp="2clheo4d8jlwc">
            <button type="button" onClick={() => setOpen(isOpen ? null : s.id)} aria-expanded={isOpen} aria-controls={panelId} className="flex w-full items-center gap-x-4 px-4 py-3.5 text-left transition-colors duration-150 ease-soft hover:bg-shell sm:px-5" data-id="element-2414" data-mp="2qicq23gylpfk">
              <span className="w-12 shrink-0 text-[14px] tabular-nums text-muted" data-id="element-2415" data-mp="2vaecujbr6yju">{clock(s.at)}</span>
              <span className="min-w-0 flex-1" data-id="element-2416" data-mp="399nfeg71du5c">
                <span className={`block truncate text-[14px] ${voided ? 'text-muted line-through' : 'text-ink'}`} data-id="element-2417" data-mp="34asyxkwjkhoe">{s.customerName}</span>
                <span className="block truncate text-[12px] text-muted" data-id="element-2418" data-mp="3nwm6ub5w8ok2">
                  {s.number} · {s.lines.length === 1 ? s.lines[0].name : `${s.lines.length} regels`}
                </span>
              </span>
              {voided ? <Badge tone="neutral" data-id="element-2419" data-mp="3exqzuwv0zafp">Gecorrigeerd</Badge> : <span className="hidden text-[12px] text-muted sm:inline" data-id="element-2420" data-mp="3iw87dhicymxn">{s.methodCents === 0 && (s.creditCents ?? 0) > 0 ? 'Tegoed' : s.voucherCents > 0 && s.methodCents === 0 ? 'Cadeaubon' : s.cashPartCents ? 'Pin + contant' : methodLabel[s.method]}</span>}
              <span className={`text-[14px] tabular-nums ${voided ? 'text-muted line-through' : 'text-ink'}`} data-id="element-2421" data-mp="39y4o6ruui290">{money(s.totalCents)}</span>
              <ChevronDownIcon className={`h-4 w-4 shrink-0 text-muted transition-transform duration-150 ease-soft ${isOpen ? 'rotate-180' : ''}`} strokeWidth={1.5} aria-hidden="true" data-id="element-2422" data-mp="305qjprcduhj5" />
            </button>
            {isOpen && <div id={panelId} className="space-y-4 border-t border-line bg-shell px-4 py-4 text-[13px] sm:px-5" data-id="element-2423" data-mp="2r5ajcqqxrqii">
                <ul className="space-y-1.5" data-id="element-2424" data-mp="2j78hw6yve5wp">
                  {s.lines.map((l, i) => <li key={i} className="flex justify-between gap-3" data-id="element-2425" data-mp="2e6t5piqbr4gb" data-mpi={i}>
                      <span className="min-w-0" data-id="element-2426" data-mp="29e03b1ibw87e">
                        <span className="text-ink" data-id="element-2427" data-mp="25dpmetszzxdr">
                          {l.kind === 'product' && l.qty > 1 ? `${l.qty} × ` : ''}
                          {l.name}
                        </span>
                        {l.kind === 'treatment' && l.fromKuur && <span className="text-muted" data-id="element-2428" data-mp="1vei7w3aj1wp2"> · uit kuur</span>}
                        {l.discountReason && <span className="block text-[12px] text-muted" data-id="element-2429" data-mp="1qgyu14hqrlxn">
                            Korting {money((l.listPriceCents - l.priceCents) * (l.kind === 'product' ? l.qty : 1))}: {l.discountReason}
                          </span>}
                      </span>
                      <span className="tabular-nums text-ink" data-id="element-2430" data-mp="3gteajohzn2g5">{money(l.totalCents)}</span>
                    </li>)}
                </ul>
                <dl className="space-y-1 border-t border-line pt-3" data-id="element-2431" data-mp="17jybmzkung3q">
                  {s.voucherCents > 0 && <div className="flex justify-between gap-3" data-id="element-2432" data-mp="1caokwnwec33p">
                      <dt className="text-muted" data-id="element-2433" data-mp="1gcihajzk6ky2">Cadeaubon …{s.voucherLast4}</dt>
                      <dd className="tabular-nums text-ink" data-id="element-2434" data-mp="1gemi2t25v3u4">{money(s.voucherCents)}</dd>
                    </div>}
                  {(s.creditCents ?? 0) > 0 && <div className="flex justify-between gap-3" data-id="element-2435" data-mp="olvt93mt4hew">
                      <dt className="text-muted" data-id="element-2436" data-mp="smo4g2f8owdt">Tegoed</dt>
                      <dd className="tabular-nums text-ink" data-id="element-2437" data-mp="spaatyqe95ug">{money(s.creditCents ?? 0)}</dd>
                    </div>}
                  {(['pin', 'contant'] as const).map(m => {
              const cents = saleMethods(s)[m];
              return cents > 0 ? <div key={m} className="flex justify-between gap-3" data-id="element-2438" data-mp="2930itgkmwe9d">
                        <dt className="text-muted" data-id="element-2439" data-mp="8xd78mwv06k1">{methodLabel[m]}</dt>
                        <dd className="tabular-nums text-ink" data-id="element-2440" data-mp="4wb2xro354b7">{money(cents)}</dd>
                      </div> : null;
            })}
                  {s.cashReceivedCents !== undefined && <div className="flex justify-between gap-3" data-id="element-2441" data-mp="dzc7vp17spda">
                      <dt className="text-muted" data-id="element-2442" data-mp="3mdb50tzxagsd">Ontvangen · wisselgeld</dt>
                      <dd className="tabular-nums text-ink" data-id="element-2443" data-mp="3hergclty2qwb">
                        {money(s.cashReceivedCents)} · {money(s.changeCents ?? 0)}
                      </dd>
                    </div>}
                </dl>
                {s.correction && <p className="rounded-md bg-sand px-3 py-2 text-ink" data-id="element-2444" data-mp="3rbmev4q7wral">
                    Gecorrigeerd om {clock(s.correction.at)}: {s.correction.reason}
                    {s.correction.refunded && ` · teruggegeven: ${[s.correction.refunded.pinCents > 0 ? `${money(s.correction.refunded.pinCents)} pin` : '', s.correction.refunded.cashCents > 0 ? `${money(s.correction.refunded.cashCents)} contant` : ''].filter(Boolean).join(' en ')}`}
                  </p>}
                <button type="button" onClick={() => void print(s)} className="inline-flex items-center gap-1.5 text-[13px] text-ink underline decoration-line underline-offset-4 hover:decoration-ink" data-id="element-2445" data-mp="1wwpb8ogb6c7e">
                  <PrinterIcon className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" data-id="element-2446" data-mp="1nvr6g3y1q0nr" />
                  Bon printen of bewaren
                </button>
                {(s.returns?.length ?? 0) > 0 && <div className="border-t border-line pt-3" data-id="element-2447" data-mp="ciycqnlp3252">
                    <p className="text-ink" data-id="element-2448" data-mp="hgq7dpdq52cy">Retour</p>
                    <ul className="mt-1.5 space-y-1.5" data-id="element-2449" data-mp="1861x1387mo38">
                      {(s.returns ?? []).map(r => <li key={r.id} className="flex items-baseline justify-between gap-3" data-id="element-2450" data-mp="143x5qb58i3jr">
                          <span className="min-w-0" data-id="element-2451" data-mp="z53oc7mttseb">
                            <span className="block truncate text-ink" data-id="element-2452" data-mp="z5lwa1iupv22">{r.lines.map(l => `${l.qty > 1 ? `${l.qty} × ` : ''}${l.name}`).join(', ')}</span>
                            <span className="block text-[12px] text-muted" data-id="element-2453" data-mp="1ruj04zmbfpba">
                              {dayShort(r.day)} {clock(r.at)} · {refundMethodLabel[r.method]} ·{' '}
                              <button type="button" onClick={() => void printReturn(r)} className="underline underline-offset-2 hover:text-ink" data-id="element-2454" data-mp="1i47wle6jni4x">
                                {r.number}
                              </button>
                              {' '}· {r.reason}
                            </span>
                          </span>
                          <span className="shrink-0 tabular-nums text-ink" data-id="element-2455" data-mp="25sr2jn24fitu">− {money(r.refundCents)}</span>
                        </li>)}
                    </ul>
                  </div>}
                <div className="flex flex-wrap items-start gap-2" data-id="element-2456" data-mp="2i4a1gv7tyfkn">
                  {/* Corrigeren (hele bon, op de dag zelf) alleen zolang er geen retour op staat. */}
                  {editable && !voided && !s.returns?.length && <VoidSaleForm sale={s} onVoided={onVoided} data-id="element-2457" data-mp="2rzuwuaie8hlf" />}
                  {canReturn && !voided && <SaleReturnForm key={`${s.id}-${s.returns?.length ?? 0}`} sale={s} onDone={onVoided} data-id="element-2458" data-mp="2w1q92a1v4fdz" />}
                </div>
              </div>}
          </li>;
    })}
    </ul>;
}